/* Goal suggestion cards: recommended feed items to hit daily tag goals.
   Items already in today's plan are greyed out. Unplanned items can be selected
   and added to the plan via a floating action button. */

import { useState, useCallback } from "react";
import { Target, Clock, Check, CalendarCheck, HelpCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDuration } from "@/lib/formatDuration";
import type { GoalSuggestion, SummaryCitation } from "../../../convex/aiSummary";

interface GoalSuggestionsSectionProps {
  goals: GoalSuggestion[];
  /** IDs of items already in today's plan (greyed out). */
  planItemIds?: Set<string>;
  /** Callback to add selected items to today's plan + queue. */
  onAddToPlan?: (ids: string[]) => void;
}

export function GoalSuggestionsSection({ goals, planItemIds, onAddToPlan }: GoalSuggestionsSectionProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleItem = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddToPlan = useCallback(() => {
    if (selectedIds.size === 0 || !onAddToPlan) return;
    onAddToPlan([...selectedIds]);
    setSelectedIds(new Set());
  }, [selectedIds, onAddToPlan]);

  if (goals.length === 0) return null;

  const planSet = planItemIds ?? new Set<string>();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 text-sm font-medium text-primary">
        <Target className="size-4" />
        Hit your goals
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="size-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-52 text-xs">
              Select items below, then click "Add to Today's Plan".
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {goals.map((goal) => (
        <GoalCard
          key={goal.goalId}
          goal={goal}
          planItemIds={planSet}
          selectedIds={selectedIds}
          onToggle={toggleItem}
        />
      ))}

      {/* "Add to Today's Plan" button — always visible */}
      {onAddToPlan && (
        <div className="flex justify-end">
        <Button
          size="sm"
          variant={selectedIds.size > 0 ? "default" : "outline"}
          className="gap-1.5"
          disabled={selectedIds.size === 0}
          onClick={handleAddToPlan}
        >
          <CalendarCheck className="size-3.5" />
          {selectedIds.size > 0
            ? `Add ${selectedIds.size} ${selectedIds.size === 1 ? "item" : "items"} to Today's Plan`
            : "Select items to add to Today's Plan"}
        </Button>
        </div>
      )}
    </div>
  );
}

interface GoalCardProps {
  goal: GoalSuggestion;
  planItemIds: Set<string>;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}

function GoalCard({ goal, planItemIds, selectedIds, onToggle }: GoalCardProps) {
  return (
    <div className="rounded-lg border border-border bg-muted/50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{goal.goalName}</span>
        <span className="text-xs text-muted-foreground">
          {goal.remainingMinutes}m remaining &middot; ~{goal.totalSuggestedMinutes}m suggested
        </span>
      </div>

      <div className="grid grid-cols-1 gap-1.5">
        {goal.suggestedItems.map((item) => (
          <SuggestionItem
            key={item.feedItemId}
            item={item}
            isInPlan={planItemIds.has(item.feedItemId)}
            isSelected={selectedIds.has(item.feedItemId)}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  );
}

interface SuggestionItemProps {
  item: SummaryCitation;
  isInPlan: boolean;
  isSelected: boolean;
  onToggle: (id: string) => void;
}

function SuggestionItem({ item, isInPlan, isSelected, onToggle }: SuggestionItemProps) {
  if (isInPlan) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-card p-2 opacity-50 cursor-default">
        <ItemThumbnail thumbnail={item.thumbnail} type={item.type} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium leading-tight line-clamp-2">{item.title}</p>
          <ItemMeta item={item} />
        </div>
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
          In Plan
        </Badge>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`flex items-center gap-2 rounded-md bg-card p-2 transition-colors text-left w-full ${
        isSelected
          ? "ring-1 ring-primary/30 bg-primary/5"
          : "hover:bg-accent"
      }`}
      onClick={() => onToggle(item.feedItemId)}
    >
      {/* Checkbox indicator */}
      <div className={`size-4 shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
        isSelected ? "bg-primary border-primary" : "border-muted-foreground/30"
      }`}>
        {isSelected && <Check className="size-2.5 text-primary-foreground" />}
      </div>
      <ItemThumbnail thumbnail={item.thumbnail} type={item.type} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium leading-tight line-clamp-2">{item.title}</p>
        <ItemMeta item={item} />
      </div>
    </button>
  );
}

function ItemThumbnail({ thumbnail, type }: { thumbnail?: string; type?: string }) {
  if (thumbnail) {
    return <img src={thumbnail} alt="" className="size-9 shrink-0 rounded object-cover" />;
  }
  return (
    <div className="size-9 shrink-0 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
      {(type ?? "").charAt(0).toUpperCase() || "?"}
    </div>
  );
}

function ItemMeta({ item }: { item: SummaryCitation }) {
  return (
    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
      {item.type && (
        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-3.5">
          {item.type}
        </Badge>
      )}
      {item.source && <span className="truncate">{item.source}</span>}
      {item.duration != null && item.duration > 0 && (
        <span className="flex items-center gap-0.5 shrink-0">
          <Clock className="size-2.5" />
          {formatDuration(item.duration)}
        </span>
      )}
    </div>
  );
}
